import axios from 'axios';
import type {
  ApiTimeDimension,
  ApiTimeDimensionVersions,
  DimensionType,
  DiscreteDimension,
  GetDimensionsArgs,
  NewDimensionArgs,
} from '~/types/api-types';

export const dimensionTypesCacheKey = 'ALL_DIMENSION_TYPES';

export async function getNewDimensionFormData() {
  const typesPromise = axios.get('/api/dimensiontypes');
  const timeDimensionsPromise = axios.get('/api/timedimensions');

  const [types, timeDimensions] = await Promise.all([typesPromise, timeDimensionsPromise]);

  return {
    types: types.data as DimensionType[],
    timeDimensions: timeDimensions.data as ApiTimeDimension[],
  };
}

export async function getDimensionTypes(): Promise<DimensionType[]> {
  const response = await axios.get<DimensionType[]>('/api/dimensiontypes');
  return response.data;
}

export async function getNewDimensionSuggestions(
  args: GetDimensionsArgs
): Promise<{ dimensions: DiscreteDimension[]; totalResults: number }> {
  try {
    const limit = args.limit !== undefined ? args.limit : 10;
    const request = await axios.get<{
      dimensions: DiscreteDimension[];
      totalResults: number;
    }>('/api/dimensions', {
      params: { ...args, limit },
    });
    return request.data;
  } catch (error) {
    throw new Error('Kunde inte hämta förslag');
  }
}

export async function getAllSubmissionYears(): Promise<ApiTimeDimension[]> {
  try {
    return (await axios.get<ApiTimeDimension[]>('/api/timedimensions/submissions')).data;
  } catch (error) {
    throw new Error('Kunde inte hämta submissioner');
  }
}

export async function getAllEmissionYears(): Promise<ApiTimeDimension[]> {
  try {
    return (await axios.get<ApiTimeDimension[]>('/api/timedimensions/emissions')).data;
  } catch (error) {
    throw new Error('Kunde inte hämta emissioner');
  }
}

export async function getAllTimeDimensionVersions() {
  try {
    const res = await axios.get('/api/versions');
    return res.data as ApiTimeDimensionVersions[];
  } catch (error) {
    throw new Error('Kunde inte hämta tidsdimensioner');
  }
}

export async function getAllDimensionTypes() {
  let cache = sessionStorage.getItem(dimensionTypesCacheKey);
  if (cache === null) {
    try {
      const res = await axios.get('/api/dimensiontypes');
      sessionStorage.setItem(dimensionTypesCacheKey, JSON.stringify(res.data));
      cache = sessionStorage.getItem(dimensionTypesCacheKey) as string;
    } catch (error) {
      throw new Error('Kunde inte hämta dimensionstyper');
    }
  }
  return JSON.parse(cache) as DimensionType[];
}

export async function createNewDimension(args: NewDimensionArgs) {
  try {
    const res = await axios.post('/api/dimensions', args);
    return res.data as DiscreteDimension;
  } catch (error) {
    throw new Error('Kunde inte skapa ny dimension');
  }
}

export async function deleteDimension(id: number) {
  try {
    await axios.delete(`api/dimensions/${id}`);
  } catch (error: any) {
    if (error.errorCode === 102) {
      throw new Error('Dimensionen kan inte tas bort då data finns kopplat till den');
    } else {
      throw new Error('Kunde inte ta bort dimensionen');
    }
  }
}

export async function clearDimensionCache() {
  await axios.delete(`api/dimensions/clear-cache`);
}